// Prepositions — the most-used ones with their core meanings, the famous
// por-vs-para split, and the personal infinitive. A frequency-ordered reference
// table of the most common prepositions sits at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const MOST_USED = [
  ["de", "of / from", "o livro da Ana · sou de Portugal"],
  ["a", "to / at", "vou a casa · às duas"],
  ["em", "in / on / at", "na mesa · em Lisboa"],
  ["com", "with", "café com leite"],
  ["por", "by / through / because of", "passo pelo parque · obrigado por tudo"],
  ["para", "for / in order to", "para ti · para aprender"],
  ["sem", "without", "sem açúcar"],
  ["sobre", "on / about", "sobre a mesa · um livro sobre arte"],
  ["entre", "between / among", "entre mim e ti"],
  ["até", "until / up to", "até amanhã"],
  ["desde", "since / from", "desde ontem"],
  ["em direção a", "toward", "em direção ao norte"],
  ["contra", "against", "contra a parede"],
  ["segundo", "according to", "segundo ela"],
  ["durante", "during", "durante a aula"],
];

export function prepositionsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Most-Used Prepositions</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${MOST_USED
            .map(
              ([pt, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${pt}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  renderGuideSections(
    app,
    "Prepositions",
    "Prepositions link words together — showing place, time, direction, or relationship. Portuguese prepositions rarely map one-to-one onto English, so it pays to learn them in context rather than by single-word translation.",
    [
      {
        title: "1. por vs para — the big one",
        body: `
          <p class="text-sage-700 mb-2">Both can translate as <i>for</i>, but they split cleanly once you see the logic.</p>
          <p class="text-sage-700 text-sm mb-1"><b>por</b> looks <i>backward</i> at cause/means — reason, exchange, duration, movement <i>through</i>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><i>Obrigado <b>pela</b> ajuda</i> (cause) · <i>Paguei 10 € <b>por</b> isto</i> (exchange)</li>
            <li><i>Estudei <b>por</b> duas horas</i> (duration) · <i>Passeio <b>pelo</b> parque</i> (through)</li>
          </ul>
          <p class="text-sage-700 text-sm mb-1"><b>para</b> looks <i>forward</i> at purpose/destination — goal, recipient, deadline:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Estudo <b>para</b> aprender</i> (purpose) · <i>É <b>para</b> ti</i> (recipient)</li>
            <li><i><b>Para</b> sexta-feira</i> (deadline) · <i>Parto <b>para</b> Lisboa</i> (destination)</li>
          </ul>
        `,
      },
      {
        title: "2. The personal infinitive",
        body: `
          <p class="text-sage-700 mb-2">Portuguese has a feature English lacks: an <b>inflected (personal) infinitive</b>. The infinitive takes a personal ending, which lets you keep the subject clear — especially after a preposition.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Forms: <i>falar, falar<b>es</b>, falar, falar<b>mos</b>, falar<b>des</b>, falar<b>em</b></i>.</li>
            <li><i>É importante <b>chegarmos</b> cedo.</i> (It's important that we arrive early.)</li>
            <li><i>Liguei-te antes de <b>saíres</b>.</i> (I called you before you left.)</li>
            <li><i>Obrigado por <b>virem</b>.</i> (Thank you all for coming.)</li>
          </ul>
        `,
      },
      {
        title: "3. Verbs that come glued to a preposition",
        body: `
          <p class="text-sage-700 mb-2">Many verbs take a fixed preposition you simply memorise with the verb:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>pensar <b>em</b></i> (think about) · <i>sonhar <b>com</b></i> (dream of)</li>
            <li><i>depender <b>de</b></i> (depend on) · <i>aprender <b>a</b></i> (learn to)</li>
            <li><i>casar <b>com</b></i> (marry) · <i>gostar <b>de</b></i> (to like)</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3">After a preposition, a verb stays in the <b>infinitive</b>: <i>antes de <b>sair</b></i> (before leaving), <i>sem <b>pensar</b></i> (without thinking) — and it can inflect (personal infinitive) when the subject differs.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
